import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useDemoMode } from '../hooks/useDemoMode';
import { buildApiUrl } from '../config/api';
import SharedMapViewer from '../components/SharedMapViewer';
import LoginButton from '../components/LoginButton';
import UserProfile from '../components/UserProfile';

const SharedMapPage = () => {
  const { shareId } = useParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { makeAuthenticatedRequest } = useAuth();
  const { isDemoMode } = useDemoMode();
  const [sharedMap, setSharedMap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [authenticatedPassword, setAuthenticatedPassword] = useState(null);

  useEffect(() => {
    if (!authLoading) {
      fetchSharedMap();
    }
  }, [authLoading, shareId]);

  const fetchSharedMap = async (providedPassword = null) => {
    try {
      setLoading(true);
      setError('');
      setPasswordError('');
      
      // Helper function to make requests with fallback to unauthenticated
      const makeRequestWithFallback = async (endpoint) => {
        // In demo mode, convert to demo endpoint
        const finalEndpoint = isDemoMode ? endpoint.replace('/api/', '/api/demo/') : endpoint;
        console.log(`[SharedMapPage] Making request to: ${finalEndpoint}`);
        console.log(`[SharedMapPage] User authenticated: ${isAuthenticated}`);
        
        // Always try unauthenticated first for shared maps (they should be public)
        try {
          console.log(`[SharedMapPage] Trying unauthenticated request...`);
          const response = await fetch(finalEndpoint);
          console.log(`[SharedMapPage] Unauthenticated response:`, {
            status: response.status,
            statusText: response.statusText,
            url: response.url,
            ok: response.ok
          });
          
          if (response.ok) {
            const contentType = response.headers.get('content-type');
            console.log(`[SharedMapPage] Success! Content-Type: ${contentType}`);
            return response;
          }
          
          // Log the error response body for debugging
          const responseText = await response.text();
          console.log(`[SharedMapPage] Unauthenticated request failed with body:`, responseText);
          
          // Create a new response with the same status but reset body
          const newResponse = new Response(responseText, {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers
          });
          
          // If unauthenticated fails and user is authenticated, try authenticated request
          if (isAuthenticated && (response.status === 401 || response.status === 403)) {
            console.log('[SharedMapPage] Unauthenticated request failed, trying authenticated...');
            try {
              const authResponse = await makeAuthenticatedRequest(finalEndpoint);
              console.log(`[SharedMapPage] Authenticated response:`, {
                status: authResponse.status,
                statusText: authResponse.statusText,
                url: authResponse.url,
                ok: authResponse.ok
              });
              return authResponse;
            } catch (authError) {
              console.log('[SharedMapPage] Authenticated request also failed:', authError.message);
              return newResponse;
            }
          }
          
          return newResponse;
        } catch (authError) {
          console.log('[SharedMapPage] Request failed with error:', authError.message);
          // If authentication fails, fall back to unauthenticated request
          try {
            console.log('[SharedMapPage] Falling back to basic fetch...');
            const fallbackResponse = await fetch(finalEndpoint);
            console.log(`[SharedMapPage] Fallback response:`, {
              status: fallbackResponse.status,
              statusText: fallbackResponse.statusText,
              ok: fallbackResponse.ok
            });
            return fallbackResponse;
          } catch (fallbackError) {
            console.error('[SharedMapPage] All requests failed:', fallbackError);
            throw fallbackError;
          }
        }
      };
      
      // Build validation endpoint with password parameter if provided
      const passwordParam = providedPassword ? `?password=${encodeURIComponent(providedPassword)}` : '';
      const validateEndpoint = buildApiUrl(`data/maps/shared/${shareId}/validate${passwordParam}`);
      
      // First validate access
      const validateResponse = await makeRequestWithFallback(validateEndpoint);
      
      if (!validateResponse.ok) {
        if (validateResponse.status === 401 || validateResponse.status === 403) {
          // Authentication required for this map
          setAccessDenied(true);
          setError('This shared map requires authentication. Please sign in to view it.');
          return;
        }
        throw new Error('Failed to validate access');
      }
      
      let validation;
      try {
        const validationText = await validateResponse.text();
        console.log(`[SharedMapPage] Validation response text:`, validationText);
        validation = JSON.parse(validationText);
        console.log(`[SharedMapPage] Parsed validation:`, validation);
      } catch (parseError) {
        console.error(`[SharedMapPage] Failed to parse validation JSON:`, parseError);
        console.error(`[SharedMapPage] Response status: ${validateResponse.status}`);
        throw new Error(`Invalid JSON response from validation endpoint: ${parseError.message}`);
      }
      
      if (!validation.has_access) {
        // Check if this is a private map that needs a password
        if (validation.reason === 'no_permission' && validation.share_type === 'private') {
          setNeedsPassword(true);
          if (providedPassword) {
            setPasswordError('Incorrect password. Please try again.');
          }
          return;
        }
        // Check if this is a restricted map that requires authentication  
        if (validation.reason === 'restricted' && !isAuthenticated) {
          setAccessDenied(true);
          setError('This shared map requires authentication. Please sign in to view it.');
          return;
        }
        // For any other access denial reasons
        setAccessDenied(true);
        setError(getAccessDeniedMessage(validation.reason));
        return;
      }
      
      // If access is valid, fetch the shared map data with password if needed
      const dataEndpoint = buildApiUrl(`data/maps/shared/${shareId}${passwordParam}`);
      const response = await makeRequestWithFallback(dataEndpoint);
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          // Authentication required for this map
          setAccessDenied(true);
          setError('This shared map requires authentication. Please sign in to view it.');
          return;
        }
        let errorData;
        try {
          const errorText = await response.text();
          console.log(`[SharedMapPage] Error response text:`, errorText);
          errorData = JSON.parse(errorText);
        } catch (parseError) {
          console.error(`[SharedMapPage] Failed to parse error JSON:`, parseError);
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        throw new Error(errorData.message || 'Failed to load shared map');
      }
      
      let data;
      try {
        const dataText = await response.text();
        console.log(`[SharedMapPage] Data response text (first 500 chars):`, dataText.substring(0, 500));
        data = JSON.parse(dataText);
        console.log(`[SharedMapPage] Parsed shared map data:`, data);
      } catch (parseError) {
        console.error(`[SharedMapPage] Failed to parse shared map JSON:`, parseError);
        console.error(`[SharedMapPage] Response status: ${response.status}`);
        throw new Error(`Invalid JSON response from shared map endpoint: ${parseError.message}`);
      }
      
      // Success - clear password states and set map data
      setNeedsPassword(false);
      setPassword('');
      setPasswordError('');
      setAuthenticatedPassword(providedPassword); // Store the successful password
      setSharedMap(data);
      
    } catch (err) {
      console.error('Error fetching shared map:', err);
      setError(err.message || 'Failed to load shared map');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setPasswordError('Please enter a password');
      return;
    }
    await fetchSharedMap(password.trim());
  };

  const getAccessDeniedMessage = (reason) => {
    switch (reason) {
      case 'not_found':
        return 'This shared map does not exist or has been removed.';
      case 'inactive':
        return 'This shared map is no longer active.';
      case 'expired':
        return 'This shared map has expired.';
      case 'no_permission':
        return 'You don\'t have permission to view this shared map.';
      default:
        return 'Access denied. Please contact the map owner for access.';
    }
  };

  if (authLoading || loading) {
    return (
      <div className="shared-map-page">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading shared map...</p>
        </div>
      </div>
    );
  }

  // Show password prompt for private maps
  if (needsPassword) {
    return (
      <div className="shared-map-page">
        <header className="app-header">
          <Link to="/" className="header-logo">
            <svg className="header-icon" viewBox="0 0 24 24">
              <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM10 5.47l4 1.4v11.66l-4-1.4V5.47zm-5 .99l3-1.01v11.7l-3 1.16V6.46zm14 11.08l-3 1.01V6.86l3-1.16v11.84z"/>
            </svg>
            <span>ShareShape Map Creator</span>
            <span className="beta-badge">BETA</span>
          </Link>
        </header>
        
        <div className="shared-map-password-required">
          <div className="password-prompt">
            <h1>🔒 Password Required</h1>
            <p>This shared map is protected by a password.</p>
            <form onSubmit={handlePasswordSubmit} className="password-form">
              <div className="form-group">
                <label htmlFor="password">Password:</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                  className={passwordError ? 'error' : ''}
                  disabled={loading}
                />
                {passwordError && (
                  <div className="error-message">{passwordError}</div>
                )}
              </div>
              <div className="form-actions">
                <button type="submit" className="btn-primary" disabled={loading}>
                  {loading ? 'Verifying...' : 'Access Map'}
                </button>
                <Link to="/" className="btn-secondary">
                  Cancel
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // Only show authentication required screen if specifically requested
  if (accessDenied && error === 'This shared map requires authentication. Please sign in to view it.') {
    return (
      <div className="shared-map-page">
        <header className="app-header">
          <Link to="/" className="header-logo">
            <svg className="header-icon" viewBox="0 0 24 24">
              <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM10 5.47l4 1.4v11.66l-4-1.4V5.47zm-5 .99l3-1.01v11.7l-3 1.16V6.46zm14 11.08l-3 1.01V6.86l3-1.16v11.84z"/>
            </svg>
            <span>ShareShape Map Creator</span>
            <span className="beta-badge">BETA</span>
          </Link>
        </header>
        
        <div className="shared-map-auth-required">
          <div className="auth-message">
            <h1>🔐 Authentication Required</h1>
            <p>Please sign in with your Microsoft account to view this shared map.</p>
            <LoginButton />
          </div>
        </div>
      </div>
    );
  }

  if (accessDenied || error) {
    return (
      <div className="shared-map-page">
        <header className="app-header">
          <Link to="/" className="header-logo">
            <svg className="header-icon" viewBox="0 0 24 24">
              <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM10 5.47l4 1.4v11.66l-4-1.4V5.47zm-5 .99l3-1.01v11.7l-3 1.16V6.46zm14 11.08l-3 1.01V6.86l3-1.16v11.84z"/>
            </svg>
            <span>ShareShape Map Creator</span>
            <span className="beta-badge">BETA</span>
          </Link>
          <div className="header-info">
            <UserProfile />
          </div>
        </header>
        
        <div className="shared-map-error">
          <div className="error-message">
            <div className="error-icon">⚠️</div>
            <h1>Access Denied</h1>
            <p>{error}</p>
            <div className="error-actions">
              <Link to="/" className="btn-primary">
                Go to Home
              </Link>
              <Link to="/map" className="btn-secondary">
                Open Map Editor
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!sharedMap) {
    return (
      <div className="shared-map-page">
        <div className="loading-screen">
          <div className="loading-spinner"></div>
          <p>Loading map data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-map-page">
      <header className="app-header shared-map-header">
        <Link to="/" className="header-logo">
          <svg className="header-icon" viewBox="0 0 24 24">
            <path d="M20.5 3l-.16.03L15 5.1 9 3 3.36 4.9c-.21.07-.36.25-.36.48V20.5c0 .28.22.5.5.5l.16-.03L9 18.9l6 2.1 5.64-1.9c.21-.07.36-.25.36-.48V3.5c0-.28-.22-.5-.5-.5zM10 5.47l4 1.4v11.66l-4-1.4V5.47zm-5 .99l3-1.01v11.7l-3 1.16V6.46zm14 11.08l-3 1.01V6.86l3-1.16v11.84z"/>
          </svg>
          <span>ShareShape</span>
          <span className="beta-badge">BETA</span>
        </Link>
        
        <div className="shared-map-info">
          <h1>{sharedMap.title}</h1>
          <div className="map-meta">
            <span className="map-creator">by {sharedMap.creator_name}</span>
            <span className="map-views">👁️ {sharedMap.view_count} views</span>
            <span className="map-type">🔗 {sharedMap.share_type}</span>
          </div>
        </div>
        
        <div className="header-actions">
          <Link to="/map" className="btn-secondary">
            Create Your Own Map
          </Link>
          <UserProfile />
        </div>
      </header>

      <div className="shared-map-content">
        <SharedMapViewer sharedMap={sharedMap} shareId={shareId} password={authenticatedPassword} />
      </div>
    </div>
  );
};

export default SharedMapPage;